import { Container, Grid, withWidth, isWidthUp, WithWidthProps } from '@material-ui/core';
import { makeStyles, createStyles, Theme as MuiTheme } from '@material-ui/core/styles';
import React, { useContext, useEffect, useState } from 'react';
import SpendingCard from './components/SpendingCard';
import PreviewCard from './components/PreviewCard';
import CardCountCard from './components/CardCountCard';
import TotalValueCard from './components/TotalValueCard';
import LinkCard from './components/LinkCard';
import { OfferingsContext } from 'core/context/OfferingsContext';
import useSafeFetch from 'common/hooks/useSafeFetch';
import { Alert } from '@material-ui/lab';
import CardsTable from './components/CardsTable';
import ActionsButton from './components/ChargeButton';
import TransactionsTable from './components/TransactionsTable';

interface DashboardProps {
  editOffering: () => void;
  redeem: () => void;
  stripeUrl: string;
}

interface OfferingMetrics {
  active_card_count: number;
  dollar_remaining_total: number;
  dollar_spend_total: number;
  purchased_card_count: number;
  transactions: any[];
}

const Dashboard: React.FC<DashboardProps & WithWidthProps> = props => {
  const classes = useStyles();
  const { offerings } = useContext(OfferingsContext);
  const { get } = useSafeFetch();
  const [metrics, setMetrics] = useState<OfferingMetrics>();
  const [error, setError] = useState<string>();

  const offeringUuid = offerings?.[0].uuid;

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await get(`${process.env.REACT_APP_PORTAL}/offerings/${offerings?.[0].uuid}/metrics`);
        setMetrics(await response.json());
      } catch (e) {
        if (!e.cancelled) {
          setError('An error occured while populating your dashboard. Please try again.');
        }
      }
    };

    fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offeringUuid]);

  const dollarTotal = metrics?.dollar_spend_total;
  const dollarsRedeemed = metrics && metrics.dollar_spend_total - metrics.dollar_remaining_total;
  const spacing = isWidthUp('sm', props.width!!) ? (isWidthUp('md', props.width!!) ? 3 : 2) : 1;

  return (
    <Container className={classes.root}>
      {error && (
        <Alert severity='error' className={classes.alert}>
          {error}
        </Alert>
      )}
      <Grid container spacing={spacing}>
        <Grid item xs={12}>
          <Grid container spacing={spacing} className={classes.top}>
            <Grid item className={classes.hiddenItem} />
            <Grid item className={classes.linkContainer}>
              <LinkCard offeringUri={offerings?.[0]?.uri} />
            </Grid>
            <Grid item className={classes.chargeButtonContainer}>
              <ActionsButton redeem={props.redeem} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={4}>
          <PreviewCard {...offerings?.[0]} editOffering={props.editOffering} />
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={spacing}>
            <Grid item xs={12}>
              <Grid container spacing={spacing}>
                <Grid item xs={12} sm={6}>
                  <CardCountCard active={metrics?.active_card_count} total={metrics?.purchased_card_count} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TotalValueCard
                    total={dollarTotal}
                    symbol={offerings?.[0]?.currency_symbol || '$'}
                    stripeUrl={props.stripeUrl}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <SpendingCard
                total={dollarTotal}
                symbol={offerings?.[0]?.currency_symbol || '$'}
                spent={dollarsRedeemed}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <TransactionsTable transactions={metrics?.transactions} />
      <CardsTable />
    </Container>
  );
};

const useStyles = makeStyles((theme: MuiTheme) =>
  createStyles({
    root: {
      padding: '2rem 0',
      color: theme.palette.text.secondary,
      [theme.breakpoints.down('md')]: {
        padding: '1rem 0'
      }
    },
    top: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      flexWrap: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        flexFlow: 'column-reverse'
      }
    },
    hiddenItem: {
      'width': '192px',
      'flexShrink': 0,
      'flexGrow': 0,
      [theme.breakpoints.down('md')]: {
        width: 0,
        padding: '0 !important'
      },
      '&.MuiGrid-item': {
        padding: 0
      }
    },
    linkContainer: {
      flexGrow: 1,
      flexShrink: 1,
      maxWidth: '100%',
      width: '100%',
      minWidth: 0
    },
    chargeButtonContainer: {
      flexShrink: 0,
      flexGrow: 0,
      width: '200px'
    },
    alert: {
      marginBottom: theme.spacing(2)
    }
  })
);

export default withWidth()(Dashboard);
